/**
 * Secure token storage utilities
 * 
 * Security improvements:
 * - Access tokens stored in-memory only (not in localStorage) to reduce XSS attack surface
 * - Refresh tokens stored in localStorage (less frequently accessed, acceptable risk)
 * - Tokens are cleared on page unload/reload for better security
 * 
 * Note: For maximum security, consider implementing httpOnly cookies on the backend
 */

interface TokenStorage {
  accessToken: string | null;
  refreshToken: string | null;
  rememberMe: boolean;
}

class SecureTokenStorage {
  // In-memory storage for access token (cleared on page reload)
  private static accessToken: string | null = null;
  
  // localStorage keys for refresh token (less sensitive, accessed less frequently)
  private static readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private static readonly REMEMBER_ME_KEY = 'rememberMe';

  /**
   * Store tokens securely
   * Access token in memory, refresh token in localStorage
   * @param tokens - Token data to store
   */
  static setTokens(tokens: TokenStorage): void {
    try {
      // Store access token in memory only
      this.accessToken = tokens.accessToken || null;
      
      // Store refresh token in localStorage (acceptable risk as it's accessed less frequently)
      if (tokens.refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
      } else {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }
      
      if (tokens.rememberMe) {
        localStorage.setItem(this.REMEMBER_ME_KEY, 'true');
      } else {
        localStorage.removeItem(this.REMEMBER_ME_KEY);
      }
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  /**
   * Get stored tokens
   * @returns Token data or null
   */
  static getTokens(): TokenStorage | null {
    try {
      const accessToken = this.accessToken;
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      const rememberMe = localStorage.getItem(this.REMEMBER_ME_KEY) === 'true';

      if (!accessToken || !refreshToken) {
        return null;
      }

      return {
        accessToken,
        refreshToken,
        rememberMe,
      };
    } catch (error) {
      console.error('Failed to retrieve tokens:', error);
      return null;
    }
  }

  /**
   * Clear all stored tokens
   * Clears both in-memory and localStorage
   */
  static clearTokens(): void {
    try {
      // Clear in-memory access token
      this.accessToken = null;
      
      // Clear localStorage
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.REMEMBER_ME_KEY);
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Check if tokens exist
   * @returns True if tokens are stored
   */
  static hasTokens(): boolean {
    const tokens = this.getTokens();
    return tokens !== null && !!tokens.accessToken && !!tokens.refreshToken;
  }

  /**
   * Update only the access token (in-memory)
   * @param accessToken - New access token
   */
  static updateAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
  }

  /**
   * Get refresh token from localStorage
   * Used for token refresh operations
   */
  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }
}

export default SecureTokenStorage;
